import React from "react";

import { TagInput, InfoTooltip, TAG_SUGGESTIONS } from "../BuilderUtils";
import { PAGE_TYPES, PAGE_TYPE_INFO } from "../pageKinds";

/**
 * FICHE DE LA PAGE — ce qui l'identifie, pas ce qu'elle montre.
 *
 * L'identifiant et le type sont figés après création : le premier est référencé
 * par les piles d'ateliers et les conséquences de décisions, le second décide
 * de la forme même du document. Les changer sur une page vivante casserait des
 * renvois qu'aucun écran ne peut réparer.
 */
export default function IdentityInspector({ page, onPatch, onChangeType, onDelete, ctx }) {
  const { isNew, usedByConfigs } = ctx;

  return (
    <>
      {!isNew && usedByConfigs !== null && (usedByConfigs || []).length > 0 && (
        <div className="builder-usage-banner">
          ⚠ Page utilisée dans <strong>{usedByConfigs.length} atelier{usedByConfigs.length > 1 ? "s" : ""}</strong> :
          {" "}{usedByConfigs.join(", ")}. Toute modification s'appliquera à tous ces ateliers.
        </div>
      )}

      <div className="builder-form-grid">
        <label className="builder-label">
          <span className="field-label-row">
            Identifiant <InfoTooltip text="Identifiant unique de la page. Utilisé dans les piles d'ateliers et les conséquences de décisions." />
          </span>
          <input
            className="builder-input"
            value={page._id}
            onChange={(e) => onPatch({ _id: e.target.value })}
            disabled={!isNew}
            placeholder="ex: intro_phase_amenagement"
          />
        </label>

        <label className="builder-label">
          <span className="field-label-row">
            Type <InfoTooltip text="Détermine l'interface affichée et les champs disponibles. Ne peut pas être modifié après création." />
          </span>
          <select
            className="builder-input"
            value={page.type}
            onChange={(e) => onChangeType(e.target.value)}
            disabled={!isNew}
          >
            {PAGE_TYPES.map((t) => (
              <option key={t} value={t}>{PAGE_TYPE_INFO[t]?.icon} {t}</option>
            ))}
          </select>
        </label>

        <label className="builder-label">
          Titre
          <input
            className="builder-input"
            value={page.title}
            onChange={(e) => onPatch({ title: e.target.value })}
            placeholder="Titre de la page"
          />
        </label>

        <label className="builder-label" style={{ gridColumn: "1 / -1" }}>
          Description
          <textarea
            className="builder-textarea"
            value={page.description}
            onChange={(e) => onPatch({ description: e.target.value })}
            rows={2}
            placeholder="Description courte affichée en en-tête de page"
          />
        </label>

        {page.type === "mosaic" && (
          <label className="builder-label">
            <span className="field-label-row">
              Round <InfoTooltip text="Numéro du round pour les pages mosaïque en mode Compte-Gouttes. Laissez vide si non applicable." />
            </span>
            <input
              className="builder-input"
              type="number"
              value={page.round || ""}
              onChange={(e) => onPatch({ round: e.target.value ? Number(e.target.value) : "" })}
              placeholder="ex: 1"
            />
          </label>
        )}
      </div>

      <div style={{ marginTop: 16 }}>
        <div className="field-label-row" style={{ marginBottom: 8 }}>
          <span className="builder-label-text">Tags</span>
          <InfoTooltip text="Tags pour catégoriser la page. Utilisés pour filtrer dans le builder." />
        </div>
        <TagInput tags={page.tags || []} onChange={(t) => onPatch({ tags: t })} suggestions={TAG_SUGGESTIONS} />
      </div>

      <PageTypeCard type={page.type} />

      {/* SUPPRIMER LA PAGE EST RARE ET DÉFINITIF : sa place est ici, dans la
          fiche, à côté de la liste des ateliers qu'elle casserait — et non dans
          la barre du haut, à un pixel du bouton qu'on utilise tous les jours.
          Retirer une page d'un atelier est un autre geste, réversible, et il a
          son propre bouton. */}
      {onDelete && (
        <section className="identity-danger">
          <h3 className="identity-danger-title">Supprimer cette page</h3>
          <p>
            Définitif, et pour tout le monde : la page disparaît de la
            bibliothèque et de {(usedByConfigs || []).length > 0
              ? `${usedByConfigs.length} atelier(s) — ${usedByConfigs.join(", ")} — dont elle cassera le parcours`
              : "tout atelier qui l'utiliserait"}.
            Pour la retirer d'un seul atelier, utilisez « Retirer de l'atelier » en haut.
          </p>
          <button className="builder-btn-danger" onClick={onDelete}>Supprimer définitivement</button>
        </section>
      )}
    </>
  );
}

/** Rappel de ce que fait ce type de page, avec ses conseils d'usage. */
export function PageTypeCard({ type }) {
  const info = PAGE_TYPE_INFO[type];
  if (!info) return null;
  return (
    <div className="page-type-info" style={{ borderLeftColor: info.color, marginTop: 18 }}>
      <div className="page-type-info-header">
        <span className="page-type-icon">{info.icon}</span>
        <strong>{info.label}</strong>
      </div>
      <p className="page-type-desc">{info.description}</p>
      {info.tips.length > 0 && (
        <ul className="page-type-tips">
          {info.tips.map((tip, i) => <li key={i}>{tip}</li>)}
        </ul>
      )}
    </div>
  );
}
